import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type BasketPosition = {
  artworkId: string;
  quantity: number;
};

type BasketStore = {
  positions: BasketPosition[];
  addPosition: (artworkId: string, maxTotalQuantity?: number | null) => boolean;
  removePosition: (artworkId: string) => void;
  updateQuantity: (
    artworkId: string,
    quantity: number,
    maxTotalQuantity?: number | null
  ) => boolean;
  clear: () => void;
  totalCount: () => number;
};

/**
 * Client-side shopping basket for the customer flow.
 *
 * Positions are {artworkId, quantity} — the full artwork is fetched on
 * demand via useGetArtwork so we never store stale artwork data. Persisted
 * to localStorage so a page reload keeps the basket, but not synced to the
 * backend (per-device only).
 *
 * addPosition merges by artworkId: adding the same artwork twice bumps
 * quantity by 1 instead of creating a second position.
 *
 * addPosition and updateQuantity take an optional maxTotalQuantity (the
 * venue's max_order_quantity, see useBasketLimit). When raising the total
 * quantity above that limit, the change is rejected and `false` is
 * returned so callers can show feedback. Lowering quantities and removals
 * are always allowed.
 */
export const useBasketStore = create<BasketStore>()(
  persist(
    (set, get) => ({
      positions: [],
      addPosition: (artworkId, maxTotalQuantity) => {
        const total = get().totalCount();
        if (maxTotalQuantity != null && total + 1 > maxTotalQuantity) {
          return false;
        }
        set((state) => {
          const existing = state.positions.find(
            (p) => p.artworkId === artworkId
          );
          if (existing) {
            return {
              positions: state.positions.map((p) =>
                p.artworkId === artworkId
                  ? { ...p, quantity: p.quantity + 1 }
                  : p
              ),
            };
          }
          return {
            positions: [...state.positions, { artworkId, quantity: 1 }],
          };
        });
        return true;
      },
      removePosition: (artworkId) =>
        set((state) => ({
          positions: state.positions.filter((p) => p.artworkId !== artworkId),
        })),
      updateQuantity: (artworkId, quantity, maxTotalQuantity) => {
        const current =
          get().positions.find((p) => p.artworkId === artworkId)?.quantity ??
          0;
        if (
          quantity > current &&
          maxTotalQuantity != null &&
          get().totalCount() - current + quantity > maxTotalQuantity
        ) {
          return false;
        }
        set((state) => {
          if (quantity <= 0) {
            return {
              positions: state.positions.filter(
                (p) => p.artworkId !== artworkId
              ),
            };
          }
          return {
            positions: state.positions.map((p) =>
              p.artworkId === artworkId ? { ...p, quantity } : p
            ),
          };
        });
        return true;
      },
      clear: () => set({ positions: [] }),
      totalCount: () =>
        get().positions.reduce((sum, p) => sum + p.quantity, 0),
    }),
    {
      name: 'basket-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
